import { useEffect, useState } from "react";
import { Check, Trash2, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const STATUS_LABELS = { pending: "En attente", approved: "Validé", rejected: "Refusé" };
const CAT_LABELS = { apartment: "Appartement", house: "Maison", villa: "Villa", plot: "Terrain", building: "Immeuble", commercial: "Commercial", approved_projects: "Projet autorisé" };

export default function MemberManager({ token, setError }) {
  const [members, setMembers] = useState(null);
  const [confirmId, setConfirmId] = useState(null);

  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const load = () =>
    fetch(`${API}/api/admin/members`, { headers })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => setMembers(d.members))
      .catch(() => setError("Chargement impossible."));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setStatus = async (id, status) => {
    try {
      const res = await fetch(`${API}/api/admin/members/${id}/status`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error("failed");
      setMembers((ms) => ms.map((m) => (m.id === id ? { ...m, status } : m)));
    } catch {
      setError("Mise à jour impossible.");
    }
  };

  const del = async (id) => {
    try {
      const res = await fetch(`${API}/api/admin/members/${id}`, { method: "DELETE", headers });
      if (!res.ok) throw new Error("failed");
      setMembers((ms) => ms.filter((m) => m.id !== id));
      setConfirmId(null);
    } catch {
      setError("Suppression impossible.");
    }
  };

  const actionCls =
    "flex items-center gap-1.5 border border-ivory/20 px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] text-ivory/60 transition-colors duration-300 hover:border-bronze hover:text-ivory";

  return (
    <div data-testid="member-manager" className="mt-2">
      <p className="text-[11px] uppercase tracking-[0.25em] text-ivory/45">
        {members ? `${members.length} membre${members.length > 1 ? "s" : ""}` : "…"}
      </p>
      {!members ? (
        <p className="mt-10 text-ivory/50">Chargement…</p>
      ) : members.length === 0 ? (
        <p data-testid="member-list-empty" className="mt-10 text-ivory/50">Aucune inscription pour le moment.</p>
      ) : (
        <div className="mt-8 divide-y divide-ivory/10 border-y border-ivory/10" data-testid="member-list">
          {members.map((mb) => (
            <div key={mb.id} data-testid={`admin-member-${mb.id}`} className="grid gap-3 px-2 py-8 transition-colors duration-300 hover:bg-ivory/[0.02] md:grid-cols-[220px_1fr_auto] md:gap-8">
              <div>
                <p className="text-sm text-ivory">
                  {mb.first_name} {mb.last_name}
                </p>
                <p className="text-xs text-ivory/50">{mb.company}</p>
                <a href={`mailto:${mb.email}`} className="text-xs text-champagne">{mb.email}</a>
                {mb.phone && <p className="text-xs text-ivory/50">{mb.phone}</p>}
              </div>
              <div className="text-xs font-light text-ivory/60">
                <p>
                  <span className="uppercase tracking-[0.15em] text-ivory/40">Intérêts : </span>
                  {mb.interests && mb.interests.length > 0
                    ? mb.interests.map((i) => CAT_LABELS[i] || i).join(", ")
                    : "Tous"}
                </p>
                {mb.message && <p className="mt-1 italic">« {mb.message} »</p>}
                <p className="mt-2 text-ivory/35">
                  Inscrit le {new Date(mb.created_at).toLocaleString("fr-CH", { dateStyle: "medium" })}
                  {mb.last_login_at &&
                    ` · Dernière connexion ${new Date(mb.last_login_at).toLocaleString("fr-CH", { dateStyle: "medium", timeStyle: "short" })}`}
                </p>
              </div>
              <div className="flex flex-col items-start gap-2 md:items-end">
                <span
                  data-testid={`member-status-${mb.id}`}
                  className={`border px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] ${
                    mb.status === "approved"
                      ? "border-champagne/50 text-champagne"
                      : mb.status === "pending"
                        ? "border-ivory/30 text-ivory/60"
                        : "border-red-400/40 text-red-300/70"
                  }`}
                >
                  {STATUS_LABELS[mb.status]}
                </span>
                <div className="flex flex-wrap gap-2">
                  {mb.status !== "approved" && (
                    <button data-testid={`member-approve-${mb.id}`} onClick={() => setStatus(mb.id, "approved")} className={actionCls}>
                      <Check size={12} /> Valider
                    </button>
                  )}
                  {mb.status !== "rejected" && (
                    <button data-testid={`member-reject-${mb.id}`} onClick={() => setStatus(mb.id, "rejected")} className={actionCls}>
                      <X size={12} /> Refuser
                    </button>
                  )}
                  {confirmId === mb.id ? (
                    <span className="flex items-center gap-2">
                      <button data-testid={`member-delete-yes-${mb.id}`} onClick={() => del(mb.id)}
                        className="border border-red-400/60 px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] text-red-300 hover:bg-red-400/10">
                        Confirmer
                      </button>
                      <button data-testid={`member-delete-no-${mb.id}`} onClick={() => setConfirmId(null)}
                        className="px-2 py-1.5 text-[10px] uppercase tracking-[0.15em] text-ivory/40 hover:text-ivory">
                        Annuler
                      </button>
                    </span>
                  ) : (
                    <button data-testid={`member-delete-${mb.id}`} onClick={() => setConfirmId(mb.id)} className={actionCls}>
                      <Trash2 size={12} /> Supprimer
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
